import React, { useContext } from 'react';
import {
  Avatar,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import CardBase from 'components/CardBase';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import PersonService from 'services/person';
import ButtonNav from './ButtonNav';
import Panel from './Panel';

const { fullname } = PersonService;

const getDependentsStyles = (theme: Theme) => ({
  avatar: {
    height: 64,
    width: 64,
  },
  card: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0',
  },
  margin: {
    margin: `${theme.spacing(1)} 0`,
  },
  row: {
    flexDirection: 'row',
  },
});

const CreateProcessModalPanelDependents: React.FC = () => {
  const { process, setProcess } = useContext(CreateProcessContext);
  const theme = useTheme();
  const styles = getDependentsStyles(theme);

  const handleSpouseSelection = (event: any) => {
    setProcess((draft) => {
      draft.employeeData!.spouse = event.target.value === 'true';
    });
  };

  const handleDependents = (dependents: number) => {
    setProcess((draft) => {
      draft.employeeData!.dependents = dependents;
    });
  };

  /* Min, Max for Number of Children */
  const min = 0;
  const max = 20;
  const disablePlus = process.employeeData?.dependents === max;
  const disableMinus = process.employeeData?.dependents === min
    || process.employeeData?.dependents === null
    || process.employeeData?.dependents === undefined;

  const handlePlus = () => {
    const dependents = process.employeeData?.dependents || 0;
    const value = dependents + 1;
    if (value <= max) {
      handleDependents(value);
    }
  };

  const handleMinus = () => {
    const dependents = process.employeeData?.dependents || 0;
    const value = dependents - 1;
    if (value >= min) {
      handleDependents(value);
    }
  };

  const canGoToNext = true;

  return (
    <Panel>
      <CardBase>
        <Typography variant="h6">
          Candidate Information
        </Typography>
        <Typography>
          If you don’t have this information feel free to skip
        </Typography>

        <Grid container sx={styles.margin}>
          <Grid size={3}>
            <Avatar sx={styles.avatar} />
          </Grid>
          <Grid size={7}>
            <Typography variant="body1">
              {fullname(process.employeeData || {})}
            </Typography>
            <Typography variant="body1">
              {process.employeeData?.email}
            </Typography>
            <Typography variant="caption">
              <IconButton disabled size="large">
                <PersonAddIcon />
              </IconButton>
              Employee type:
              {' '}
              <strong>New Employee</strong>
            </Typography>
          </Grid>
        </Grid>

        <Grid container sx={styles.margin}>
          <Grid size={6}>
            <Typography sx={styles.margin}>
              Do they have a spouse?
            </Typography>
            <RadioGroup
              sx={styles.row}
              value={String(process.employeeData?.spouse)}
              onChange={handleSpouseSelection}
            >
              <FormControlLabel
                value="true"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="primary" />}
                label="No"
              />
            </RadioGroup>
          </Grid>
          <Grid size={6}>
            <Typography sx={styles.margin}>
              How many children?
            </Typography>
            <CardBase sx={styles.card}>
              <IconButton onClick={handleMinus} disabled={disableMinus} size="large">
                <RemoveIcon color={disableMinus ? 'disabled' : 'primary'} />
              </IconButton>
              <Typography>
                {process.employeeData?.dependents || 0}
              </Typography>
              <IconButton
                onClick={handlePlus}
                disabled={process.employeeData?.dependents === max}
                size="large">
                <AddIcon color={disablePlus ? 'disabled' : 'primary'} />
              </IconButton>
            </CardBase>
          </Grid>
        </Grid>
      </CardBase>
      <ButtonNav disabled={!canGoToNext} />
    </Panel>
  );
};

export default CreateProcessModalPanelDependents;
